import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  useRunCmsReportingSubmission, useCmsSubmissionPoll, useCaptureCmsBaseline, useCmsSubmissions,
} from '../state/data/cms';
import { successfulReports } from '../logic/cmsWindows';
import {
  overrideAccepted, OVERRIDE_MIN_REASON, daysToBaselineDeadline, requirementBannerCopy,
} from '../logic/cmsRequiredMeasures';
import { useQueryClient } from '@tanstack/react-query';
import { freshTerminalRow } from '../logic/cmsResultCodes';
import { CMS_KEYS } from '../state/data/cms';
import { TASK_KEYS } from '../state/data/task';
import { scheduleInvalidate } from '../state/data/_shared/wf-race';
import { useCurrentUser } from '../state/data/account';
import { completionActorOf } from '../logic/task';
import { bpReadingsHeader, bpReadingWhen } from '../logic/bpSession';
import {
  MANUAL_OBSERVATIONS, PAIRED_SLUG, hasManualEntries, manualDateError, manualDraftErrors,
  manualObservationsPayload, metaFor, applySuggestions, provenanceOf,
  entryUnitFor, entryBounds, metricToEntry, entryToMetric, hasUsEntryUnit,
  compositeEntryFor, totalInchesOf, feetInchesOf, compositeIncomplete,
} from '../logic/cmsManualObservation';
import { VerdictBanner } from './cms/cmsModalParts';

/**
 * CmsReportingModal — operator-triggered CMS ACCESS Model reporting submission
 * for rpt_*_submission tasks (baseline / quarterly / final).
 *
 * Fires the Tier 4 cms_reporting_requested event → WF37 builds a FHIR Bundle
 * (Patient + Practitioner + Organization + Composition + N Observations per the
 * ACCESS Model IG) and POSTs to /Patient/$report-data. Result is one of seven
 * ACCESSReportDataResultCS codes (see RESULT_CODE_LABELS below).
 *
 * On `success` or `duplicate`, WF37 PATCHes the originating task to `completed`.
 *
 * Props:
 *   patient       — { userid, first_name?, last_name?, firstname?, lastname? }
 *   carePeriodId  — uuid of the active care period
 *   taskId        — uuid of the rpt_*_submission task instance
 *   reportKind    — 'baseline' | 'quarterly' | 'final'
 *   onClose()
 *   onSaved()
 */

/* ── ACCESSReportDataResultCS → friendly explanation map ─────────────────
 * Codes per CMMI ACCESS Model IG v0.9.6 DRAFT
 * (github.com/DSACMS/cmmi-access-model/CodeSystem-ACCESSReportDataResultCS).
 */
const RESULT_CODE_LABELS = {
  'success': {
    headline: 'Submission accepted',
    // Whether the task actually closed is reported by the "System changes" section, off WF37's
    // side_effects — the PATCH can fail after CMS has accepted, so this copy must not claim it.
    explainer: 'CMS received the report for this reporting period.',
    tone: 'good',
  },
  'duplicate': {
    headline: 'Duplicate · already on file',
    explainer: 'CMS already has a report for this period, so the data is already there. No further action.',
    tone: 'moderate',
  },
  'validation-error': {
    headline: 'Validation error',
    explainer: 'CMS rejected the submission as malformed. See the issues below; fix the underlying data and retry.',
    tone: 'moderate',
  },
  'incomplete-data': {
    headline: 'Incomplete data',
    explainer: 'Required observations are missing from this submission. See the issues below; collect the missing measurements / labs and retry.',
    tone: 'moderate',
  },
  'patient-not-aligned': {
    headline: 'Patient not aligned',
    explainer: 'Patient is not currently aligned to this participant in this track. Complete CMS alignment first; reporting submissions are only accepted for aligned beneficiaries.',
    tone: 'bad',
  },
  'reporting-period-closed': {
    headline: 'Reporting period closed',
    explainer: 'The reporting window for this period has closed — CMS will not accept further submissions for it. Escalate to the care coordinator team for a remediation path.',
    tone: 'bad',
  },
  'incorrect-track': {
    headline: 'Track mismatch',
    explainer: 'The bundle\'s track does not match the patient\'s aligned track. This is most likely a system bug — flag to platform-eng before retrying.',
    tone: 'bad',
  },
  /* WF37-side aborts — no CMS call was made in any of these. */
  /* 'baseline-not-fresh' was retired from WF37 on 2026-08-08 — bp-readings-insufficient below is
     its correct implementation (it counts the actual readings, on every report kind). A WF37 still
     running the old build falls through to the generic resultMeta branch, which renders CMS's own
     display + reason, so the transition needs no shim here.
     RFA Appendix C p55: "Each submission to CMS must reflect an average of at least three
     readings." Blocks every report kind, not just the baseline. There is deliberately no manual
     path out of this one — the same bullet forbids manual BP entry, so the only fix is real
     readings. `result.reason` (rendered below) carries the actual count. */
  'bp-readings-insufficient': {
    headline: 'Not enough blood-pressure readings',
    explainer: 'Nothing was sent. CMS needs at least 3 cuff readings from the last 15 days, and this program files 3 taken on the same day; BP cannot be entered by hand. Ask the patient for 3 readings in a row on one day, then re-run. A blocked submission is safe to retry.',
    tone: 'moderate',
  },
  /* The care period could not be read, so the beneficiary's CMS track is unknown. WF37 refuses
     rather than guess: a guessed track is what CMS rejects as 'incorrect-track' (2026-08-11). */
  'care-period-unresolved': {
    headline: 'Care period could not be read',
    explainer: 'Nothing was sent. The care period behind this report could not be loaded, so the patient\'s CMS track is unknown and the bundle cannot be built. Re-running is safe; if it persists, flag to platform-eng.',
    tone: 'bad',
  },
  'manual-entry-invalid': {
    headline: 'Entered values were rejected',
    explainer: 'Nothing was saved and nothing was submitted. Correct the values listed below and re-run.',
    tone: 'moderate',
  },
  'cms-not-configured': {
    headline: 'CMS endpoint not configured',
    explainer: 'This environment has no CMS API base set, so there was nothing to submit to. Nothing was sent and nothing was recorded as submitted. Ask platform-eng to set $vars.CMS_API_BASE on this n8n project.',
    tone: 'bad',
  },
  'error': {
    headline: 'Couldn\'t read CMS\'s answer',
    explainer: 'The reporting endpoint replied with something that isn\'t a CMS verdict, so we cannot tell you whether the report was accepted. Treat it as NOT submitted, and check the WF37 execution before retrying.',
    tone: 'bad',
  },
  /* Cap expired — CMS has the submission, we just never saw the verdict. NOT a failure:
     telling the operator to "try again" here produces a second CMS call whose duplicate
     draft row collides with the single-in-flight index and is swallowed. Copy mirrors
     LIFECYCLE_LABELS.pending, which the align/unalign modals already use. */
  'pending': {
    headline: 'CMS still processing',
    explainer: 'CMS accepted the submission but had not returned a verdict when we stopped waiting. Do NOT re-submit — the result will land on the submission record. The care team can check it there, or re-open this task later.',
    tone: 'moderate',
  },
  'manual-entry-not-recorded': {
    headline: 'Values could not be saved — nothing was submitted',
    explainer: 'The values were valid but could not be written to the patient record, so the report was deliberately NOT submitted. Re-running is safe.',
    tone: 'bad',
  },
  /* CMS's quarterly window: 70 to 110 days after the last accepted report. WF37 checks it before
     anything is recorded or sent; `result.reason` carries the dates. */
  'quarterly-window-not-open': {
    headline: 'Quarterly window not open yet',
    explainer: 'Nothing was sent. CMS accepts a quarterly report only from 70 days after the last accepted report. File on the opening date, with readings from the 15 days before it.',
    tone: 'moderate',
  },
  'quarterly-window-closed': {
    headline: 'Quarterly window closed',
    explainer: 'Nothing was sent. CMS accepts a quarterly report only up to 110 days after the last accepted report, and that date has passed. Ask CMS how to proceed before filing.',
    tone: 'bad',
  },
  'quarterly-file-as-one': {
    headline: 'File the quarter as one report',
    explainer: 'Nothing was sent. Each quarter is filed once, with blood pressure and weight together. Open the quarterly report again from the task or the patient file: it sends both.',
    tone: 'moderate',
  },
  'report-history-unreadable': {
    headline: 'Past reports could not be read',
    explainer: 'Nothing was sent. The CMS reports already filed for this patient could not be loaded, so the quarterly window cannot be checked. Re-running is safe; if it persists, flag to platform-eng.',
    tone: 'bad',
  },
};

/* ── report_kind → human label ──────────────────────────────────────── */
const REPORT_KIND_LABELS = {
  'baseline':         { label: 'Baseline submission',         hint: 'Day-60 baseline measures + lab panel' },
  'quarterly':        { label: 'Quarterly submission',        hint: 'Blood pressure, weight and BMI for the quarter window' },
  'final':            { label: 'Final submission',            hint: 'End-of-period measures + closing labs' },
};

/* ── Observation slug → human label ─────────────────────────────────── */
const OBSERVATION_LABELS = {
  'systolic_bp': 'Blood pressure',
  'weight':      'Body weight',
  'bmi':         'BMI',
  'waist':       'Waist circumference',
  'hba1c':       'HbA1c',
  'egfr':        'eGFR',
  'uacr':        'UACR',
  'ldl':         'LDL cholesterol',
};

/* ── Why BMI is missing ─────────────────────────────────────────────────
   BMI is DERIVED by WF37 from the weight it is sending and the patient's height (RFA Appendix C
   p56 defines it as weight(kg)/height(m²)) — so it is missing for a reason, and the reason decides
   what the operator can do about it. 89 of 91 care periods shipped weight with BMI silently absent
   for months and nobody noticed, which is what an unexplained blank buys. */
const BMI_OMISSION = {
  'no-height': {
    text: 'No height on file, so BMI can’t be calculated. Enter it once below — it is saved to the patient record and reused for every future submission.',
    offerHeight: true,
  },
  'no-weight': {
    text: 'No weight to calculate from. Enter a weight above and BMI follows automatically.',
    offerHeight: false,
  },
  'implausible': {
    // NOT offerHeight: WF37 only reaches 'implausible' with an in-band height (1.2-2.2 m), so the
    // suspect number is the weight — re-typing the height would not move it.
    text: 'The weight and height on file give an implausible BMI, so it was left out rather than sent. Check both values before submitting.',
    offerHeight: false,
  },
};

/* hasOwn, for the same reason metaFor does it: a reason of 'constructor' resolves through the
   prototype and would otherwise hand back a function to read `.text` off. */
const omission = (reason) => (Object.hasOwn(BMI_OMISSION, reason ?? '') ? BMI_OMISSION[reason] : {});

/* ── Loading-step lists (timed reveal) ──────────────────────────────── */
const PREVIEW_LOADING_STEPS = [
  { at: 0,    label: 'Reading patient context from the Platform API' },
  { at: 1500, label: 'Compiling FHIR Bundle from latest observations' },
  { at: 3500, label: 'Preparing for operator review' },
];
// How long to wait for cms_submissions to show the verdict after the caller timed out.
// WF37's own CMS poll caps at 30s; this covers that plus n8n queueing.
const VERDICT_POLL_TIMEOUT_MS = 90_000;

const SUBMIT_LOADING_STEPS = [
  { at: 0,     label: 'Authenticating with CMS' },
  { at: 1500,  label: 'Submitting FHIR Bundle to /Patient/$report-data' },
  { at: 6000,  label: 'Awaiting CMS confirmation' },
  // Past ~15s the browser's own call has already timed out and we are reading the
  // submission record instead. Say so rather than leaving a spinner that looks stuck.
  { at: 16000, label: 'Still with CMS — reading the result from the submission record' },
];

export default function CmsReportingModal({
  patient, carePeriodId, taskId, reportKind, onClose, onSaved,
  // Injectable only so the give-up path is testable without fake timers (userEvent needs a
  // running clock). Never set in app code.
  verdictTimeoutMs = VERDICT_POLL_TIMEOUT_MS,
}) {
  // 'preview-loading' → building the Bundle (read-only) for operator review
  // 'preview'         → Bundle ready; operator confirms or cancels
  // 'submit-loading'  → actual CMS POST in flight
  // 'result'          → submission verdict (success / failure / etc.)
  // 'error'           → network/transport failure on either call
  const [phase, setPhase] = useState('preview-loading');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [copied, setCopied] = useState(null); // 'verdict' | 'reportid' | null
  const [resultVisible, setResultVisible] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  // slug -> { value, collectedAt } typed by the operator for a missing observation.
  const [manualDrafts, setManualDrafts] = useState({});
  // The manual payload the currently-displayed preview was built from, so an edited
  // value can't be submitted under a bundle the operator never saw.
  const [previewedManual, setPreviewedManual] = useState('[]');
  // Written justification for filing with a REQUIRED measure missing. Collapsed by default:
  // the intended action is to enter the values, not to wave the gate through.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const overrideReady = overrideAccepted({ reason: overrideReason });
  // A submit outlives apiv2's clinical_event sync budget (WF37 takes ~12-19s), so the
  // browser sees a 522 for a submission CMS is already processing. When that happens we do
  // NOT know the outcome — so poll cms_submissions for the verdict rather than calling it a
  // failure. Same fire-and-poll shape the eligibility/align/unalign modals already use.
  const [awaitingVerdict, setAwaitingVerdict] = useState(false);
  const [baselineIds, setBaselineIds] = useState(() => new Set());
  const captureBaseline = useCaptureCmsBaseline('reporting');
  const pendingErrorRef = useRef(null);   // the original throw, kept for the detail line
  // Cap timer armed once at fire time, exactly like CmsUnalignmentModal — NOT inside the
  // verdict effect, where a change in the polled data would silently re-arm it.
  const capTimerRef = useRef(null);
  const settledRef = useRef(false);
  const aliveRef = useRef(true);
  const qc = useQueryClient();
  // CMS accepts a duplicate $report-data as a fresh success — no server-side dedupe (IMPL
  // 2026-08-07: two accepted baselines for the same period, minutes apart). The window math
  // only gates the NEXT report kind, so nothing warns before filing the same one twice.
  const { data: allSubmissions } = useCmsSubmissions(patient.userid);
  const priorReports = useMemo(() => successfulReports(allSubmissions), [allSubmissions]);
  const priorDate = useMemo(() => {
    const r = priorReports[0];
    const iso = r && (r.finished_at || r.submitted_at || r.created || r.created_at);
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isFinite(t)
      ? new Date(t).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
      : null;
  }, [priorReports]);
  const { data: session } = useCurrentUser();
  const reportingMut = useRunCmsReportingSubmission();
  // `mutateAsync` is referentially stable across renders (TanStack v5);
  // the mutation wrapper object is not. Capture mutateAsync only.
  const submitReport = reportingMut.mutateAsync;

  // manualObservations is an argument, not a dep: callWorkflow must stay referentially
  // stable so the mount-effect below doesn't re-fire on every keystroke.
  const callWorkflow = useCallback(async (mode, manualObservations = [], overrideIncomplete = null) => {
    const loadingPhase = mode === 'preview' ? 'preview-loading' : 'submit-loading';
    setPhase(loadingPhase);
    setError(null);
    setStepIndex(0);
    setResultVisible(false);
    setShowRawJson(false);
    // Snapshot the existing report_data rows BEFORE firing, so the poll can identify OUR row
    // by an id that did not exist yet (skew-immune — never a wall-clock guess).
    let baseline = new Set();
    if (mode === 'submit') {
      baseline = await captureBaseline(String(patient.userid));
      if (!aliveRef.current) return;
      setBaselineIds(baseline);
    }
    try {
      const res = await submitReport({
        withingsUserId: patient.userid,
        carePeriodId,
        taskId,
        reportKind,
        mode,
        manualObservations,
        // The name is on the user record, the id on the account; WF37 audits both as "entered by".
        actor: completionActorOf(session?.currentUser && { ...session.currentUser, accountid: session.account?.accountid }),
        // Never on a preview: a preview makes no CMS call, so overriding there would only
        // hide the very list the operator is being asked to act on.
        overrideIncomplete: mode === 'submit' ? overrideIncomplete : null,
      });
      if (!aliveRef.current) return;
      setPreviewedManual(JSON.stringify(manualObservations));
      setResult(res);
      // Prefill only: the proposed values land in the same editable fields a typed one uses, which
      // makes the drafts dirty, so Confirm stays disabled until the operator re-checks. No auto
      // re-preview — that would be the machine acting on a proposal nobody has read.
      setManualDrafts((prev) => applySuggestions(prev, res?.elation_lab_suggestions));
      // The RESPONSE decides the phase, not the request: WF37 answers a submit in preview
      // mode when it aborted (invalid entry, a record write that failed, stale baseline).
      // Landing on the review screen keeps the operator's values on screen to correct —
      // 'result' would show a Done button for a submission that never happened.
      const nextPhase = (mode === 'preview' || res?.mode === 'preview') ? 'preview' : 'result';
      setPhase(nextPhase);
      requestAnimationFrame(() => {
        if (aliveRef.current) setResultVisible(true);
      });
    } catch (err) {
      if (!aliveRef.current) return;
      const message = err.message || 'CMS reporting submission failed';
      // A PREVIEW makes no CMS call, so a failure there is simply a failure. A SUBMIT may
      // already be at CMS — the caller timing out says nothing about the outcome, and
      // reporting "we couldn't reach CMS" for an accepted Medicare filing is the worst of
      // the available lies. Wait for the record to tell us.
      if (mode === 'submit') {
        pendingErrorRef.current = message;
        settledRef.current = false;
        if (capTimerRef.current) clearTimeout(capTimerRef.current);
        // Fail-SOFT at the cap: CMS has the submission and we simply stopped watching.
        // Reporting a failure here would tell the operator to re-submit, producing a second
        // CMS call whose duplicate draft row is swallowed by the single-in-flight index.
        capTimerRef.current = setTimeout(() => {
          if (!aliveRef.current || settledRef.current) return;
          settledRef.current = true;
          setAwaitingVerdict(false);
          setResult({
            mode: 'submit',
            result_code: 'pending',
            display: null,
            reason: pendingErrorRef.current,
            issues: [],
            cms_report_id: null,
            from_record: true,
          });
          setPhase('result');
          scheduleInvalidate(qc, TASK_KEYS.all);
          scheduleInvalidate(qc, CMS_KEYS.all);
          requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
        }, verdictTimeoutMs);
        setAwaitingVerdict(true);
        setPhase('submit-loading');
        return;
      }
      setError(message);
      setPhase('error');
    }
  }, [patient.userid, carePeriodId, taskId, reportKind, submitReport, session, captureBaseline, qc, verdictTimeoutMs]);

  // ── Verdict poll (only while a submit is unaccounted for) ──────────────────
  const { data: polledRows } = useCmsSubmissionPoll('reporting', String(patient.userid || ''), {
    enabled: awaitingVerdict,
    baselineIds,
  });

  useEffect(() => {
    if (!awaitingVerdict || settledRef.current) return;
    const row = freshTerminalRow(polledRows, baselineIds, 'reporting');
    if (!row) return;
    // The record answered. It carries the verdict but not WF37's rich sync payload (bundle
    // detail, per-observation table), so render the banner and mark the result as
    // record-sourced rather than inventing the parts we do not have.
    settledRef.current = true;
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    setAwaitingVerdict(false);
    setResult({
      mode: 'submit',
      result_code: row.result_code || row.status || 'error',
      display: row.result_display || null,
      reason: null,
      issues: [],
      cms_report_id: row.cms_report_id || null,
      from_record: true,
    });
    setPhase('result');
    // The mutation REJECTED, so its onSuccess invalidations never ran — the drawer CMS card
    // and the cmsWindows in-flight math would keep the pre-submit rows.
    scheduleInvalidate(qc, TASK_KEYS.all);
    scheduleInvalidate(qc, CMS_KEYS.all);
    requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
  }, [awaitingVerdict, polledRows, baselineIds, qc]);

  const manualPayload = useMemo(() => manualObservationsPayload(manualDrafts), [manualDrafts]);
  const draftErrors = useMemo(() => manualDraftErrors(manualDrafts), [manualDrafts]);
  const hasDraftErrors = Object.keys(draftErrors).length > 0;
  const hasDrafts = hasManualEntries(manualDrafts);
  // Half a height is not a draft, so nothing else on this screen can tell the operator they left
  // one box empty — Confirm would file the report without the height they think they entered.
  const compositePending = compositeIncomplete(manualDrafts);
  const manualDirty = JSON.stringify(manualPayload) !== previewedManual;

  // `patch` may be a function of the draft as it is IN STATE: a composite entry (height's two
  // boxes) derives `value` from its sibling box, which a props snapshot would read one tick stale.
  const handleDraftChange = useCallback((slug, patch) => {
    setManualDrafts((prev) => {
      const before = { value: '', collectedAt: '', ...(prev[slug] || {}) };
      return { ...prev, [slug]: { ...before, ...(typeof patch === 'function' ? patch(before) : patch) } };
    });
  }, []);

  const handleConfirmSubmit = useCallback(() => {
    callWorkflow('submit', manualPayload, overrideReady ? { reason: overrideReason } : null);
  }, [callWorkflow, manualPayload, overrideReady, overrideReason]);
  const handleRunPreview = useCallback(() => { callWorkflow('preview', manualPayload); }, [callWorkflow, manualPayload]);

  const didFireRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    // StrictMode double-invokes mount effects in dev; the guard restricts the
    // mount-effect to one run so two parallel WF37 calls don't race and clobber
    // the good result with a null body. Safe because parent (PatientDetail)
    // re-mounts on patient/task/reportKind change.
    if (!didFireRef.current) {
      didFireRef.current = true;
      callWorkflow('preview');
    }
    return () => { aliveRef.current = false; };
  }, [callWorkflow]);

  // Unmount ONLY. This must not live in the effect above: that one is keyed on callWorkflow,
  // so any re-render changing its identity would clear a cap that is legitimately armed and
  // the give-up path would never fire.
  useEffect(() => () => { if (capTimerRef.current) clearTimeout(capTimerRef.current); }, []);

  // Loading-step animation
  useEffect(() => {
    const isLoading = phase === 'preview-loading' || phase === 'submit-loading';
    if (!isLoading) return undefined;
    const steps = phase === 'preview-loading' ? PREVIEW_LOADING_STEPS : SUBMIT_LOADING_STEPS;
    const timers = steps.map((s, i) =>
      setTimeout(() => { if (aliveRef.current) setStepIndex(i); }, s.at)
    );
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  useEffect(() => {
    // A submission is at CMS and we are waiting on its verdict — an accidental Escape would
    // discard the only place that verdict is going to surface.
    const onKey = (e) => { if (e.key === 'Escape' && !awaitingVerdict) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, awaitingVerdict]);

  /* ── Derived ──────────────────────────────────────────────────────── */
  const submitted = result?.submitted === true;
  const issues = Array.isArray(result?.issues) ? result.issues : [];
  const payload = result?.submitted_payload_summary || {};
  const fetchErrors = Array.isArray(payload.fetch_errors) ? payload.fetch_errors : [];
  const manualRecorded = Array.isArray(payload.manual_recorded) ? payload.manual_recorded : [];
  const sideEffects = result?.side_effects || {};
  const reportKindLabel = REPORT_KIND_LABELS[reportKind] || { label: 'CMS report submission', hint: '' };

  const resultMeta = result
    ? RESULT_CODE_LABELS[result.result_code] || {
        headline: result.display || (submitted ? 'Submitted' : 'Submission failed'),
        explainer: result.reason || '',
        tone: submitted ? 'good' : 'bad',
      }
    : null;

  const patientName = [
    patient.firstname || patient.first_name,
    patient.lastname || patient.last_name,
  ].filter(Boolean).join(' ').trim();

  const checkedAtLabel = result?.checked_at ? formatTimestamp(result.checked_at) : null;
  // WF37 answered a preview with a rejection rather than a reviewable bundle — show why,
  // on the same screen as the inputs, instead of the generic "ready to submit" banner.
  const previewAbort = phase === 'preview' && !!result
    && result.result_code !== 'preview' && !!result.result_code;

  // WF37's aborts split in two, and the footer must not treat them alike.
  //  · manual-entry-* are CORRECTABLE here — fix the values, press Confirm, it submits.
  //  · bp-readings-insufficient is not: only real cuff readings clear it and BP cannot be typed.
  //    Confirm still RECORDS anything entered above (WF37's recorder runs before the BP gate), so
  //    the button stays live — it just stops claiming it will submit.
  //  · Every code below aborts in Parse, BEFORE the recorder, so Confirm does nothing at all.
  //    WF37's quarterly refusals must stay there: past the recorder they would save values for a
  //    report CMS will not take.
  const PARSE_ABORT_TOOLTIP = {
    'cms-not-configured': 'This environment has no CMS endpoint configured — there is nothing to submit to',
    'care-period-unresolved': 'The care period behind this report could not be read, so the bundle cannot be built — nothing to submit',
    'quarterly-window-not-open': 'CMS does not accept this quarterly report yet — the window opening date is shown above',
    'quarterly-window-closed': 'The CMS window for this quarterly report has closed — nothing can be submitted from here',
    'quarterly-file-as-one': 'A quarter is filed as one report with blood pressure and weight together — reopen the quarterly report',
    'report-history-unreadable': 'The CMS reports already filed could not be read, so the quarterly window cannot be checked',
  };
  // Only the baseline has the 60-day cliff; a quarterly/final override is not racing it.
  const deadlineDays = reportKind === 'baseline'
    ? daysToBaselineDeadline(result?.cms_alignment_date)
    : null;
  const blockedSubmitLabel = !previewAbort ? null
    : result.result_code === 'required-measures-missing' ? (overrideReady ? 'Submit incomplete to CMS' : 'Save entered values (submission blocked)')
      : result.result_code === 'bp-readings-insufficient' ? 'Save entered values (submission blocked)'
      : result.result_code === 'cms-not-configured' ? 'Cannot submit — no CMS endpoint'
        : result.result_code === 'care-period-unresolved' ? 'Cannot submit — care period unavailable'
          : result.result_code === 'quarterly-window-not-open' ? 'Cannot submit — window not open yet'
            : result.result_code === 'quarterly-window-closed' ? 'Cannot submit — window closed'
              : result.result_code === 'quarterly-file-as-one' ? 'Cannot submit — file the quarter as one report'
                : result.result_code === 'report-history-unreadable' ? 'Cannot submit — past reports unavailable'
                  : null;
  const submitBlocked = blockedSubmitLabel !== null;
  const manualBpCount = Number(result?.bp_sent_manual || 0);
  // Counted off the FORM, not the server's proposal: `manualObservationsPayload` keeps
  // `source_claimed` only while the value and date still match what the chart offered, so a
  // cleared or edited prefill stops being claimed as a chart read.
  const fromChartCount = manualPayload.filter((e) => e.source_claimed).length;
  // Slugs actually holding a validated value — the only condition under which suppressing a
  // "this is in the chart but unusable" line is honest.
  const filledSlugs = useMemo(() => new Set(manualPayload.map((e) => e.slug)), [manualPayload]);
  // Recomputed against the FORM, not the verdict: once the missing measures are sitting in the
  // fields, "enter the values below" is an instruction the operator has already carried out, and
  // the banner ends up contradicting the callout under it. `manualPayload` (validated) is the
  // source, so a value the guard would reject does not count as an answer.
  // Scoped to the required-measure block. `required_missing` is emitted on every preview, but on a
  // `bp-readings-insufficient` one the block is a CMS transmission rule manual entry can never
  // clear — softening that headline to "Nothing sent yet — all required values are filled in" because the labs are
  // filled would tell the operator the opposite of the truth.
  const requirementBanner = useMemo(() => (result?.result_code !== 'required-measures-missing' ? null : requirementBannerCopy({
    requiredMissing: Array.isArray(result?.required_missing) ? result.required_missing : [],
    filled: filledSlugs,
    labelOf: (slug) => metaFor(slug)?.label || slug,
    reason: result?.reason,
    fromChart: fromChartCount,
  })), [result?.result_code, result?.required_missing, result?.reason, filledSlugs, fromChartCount]);
  // The banner prints result_code as a small monospace chip for support. On
  // `required-measures-missing` that chip is the ONLY thing left on screen still saying "missing"
  // once the prefill has put the values in the fields — WF37's own copy already reads "…are in the
  // Elation chart", and the values are visible right below it. A coordinator reads the chip as the
  // screen contradicting itself (reported on PROD 28543097). Suppressed for THIS code only: every
  // other verdict keeps its chip, and the headline still names the measures, so support is not
  // blind (the raw-JSON view carries only the FHIR bundle, not the result).
  const bannerCode = result?.result_code === 'required-measures-missing' ? null : result?.result_code;
  const confirmIsNoop = previewAbort && Object.hasOwn(PARSE_ABORT_TOOLTIP, result.result_code);

  /* ── Footer + copy helpers ────────────────────────────────────────── */
  const handleDone = () => { onSaved?.(); onClose(); };

  const handleCopyVerdict = async () => {
    if (!result) return;
    const who = patientName || patient.userid;
    const summary = `${who}: CMS ${reportKindLabel.label.toLowerCase()} · ${result.result_code}` +
      (result.cms_report_id ? ` (reportId ${result.cms_report_id})` : '') +
      (result.checked_at ? ` on ${new Date(result.checked_at).toLocaleDateString('en-US')}` : '');
    try {
      await navigator.clipboard.writeText(summary);
      setCopied('verdict');
      setTimeout(() => { if (aliveRef.current) setCopied(null); }, 1800);
    } catch { /* clipboard unavailable */ }
  };

  const handleCopyReportId = async () => {
    if (!result?.cms_report_id) return;
    try {
      await navigator.clipboard.writeText(result.cms_report_id);
      setCopied('reportid');
      setTimeout(() => { if (aliveRef.current) setCopied(null); }, 1800);
    } catch { /* clipboard unavailable */ }
  };

  /* ── Render ───────────────────────────────────────────────────────── */
  return createPortal((
    <div className="modal-overlay" onClick={awaitingVerdict ? undefined : onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 620,
          width: '100%',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="header3" style={{ margin: 0 }}>CMS Reporting Submission</h2>
            <p className="detail1" style={{ margin: '4px 0 0', color: 'var(--wi-text-secondary)' }}>
              Medicare $report-data · {reportKindLabel.label}
              {patientName && <> · {patientName}</>}
            </p>
            {reportKindLabel.hint && (
              <p className="detail1" style={{ margin: '2px 0 0', color: 'var(--wi-text-placeholder)' }}>
                {reportKindLabel.hint}
              </p>
            )}
          </div>
          <button
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
            style={{ position: 'static', flexShrink: 0 }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 28px' }}>
          {phase === 'preview-loading' && (
            <LoadingPanel
              title="Building the FHIR Bundle for review…"
              stepIndex={stepIndex}
              steps={PREVIEW_LOADING_STEPS.map(s => s.label)}
              footnote="No CMS call yet — you'll confirm the payload before anything is sent."
            />
          )}

          {phase === 'submit-loading' && (
            <LoadingPanel
              title="Submitting report to CMS…"
              stepIndex={stepIndex}
              steps={SUBMIT_LOADING_STEPS.map(s => s.label)}
              footnote="CMS may take up to 90 seconds to confirm receipt."
            />
          )}

          {phase === 'preview' && result && (
            <div style={{
              opacity: resultVisible ? 1 : 0,
              transition: 'opacity 200ms ease',
              // One rhythm for the whole panel. Without it the blocks butt up against each other
              // (a warning banner, a heading and a data card with no air between them read as one
              // dense slab), and each block had to carry its own margin to compensate.
              display: 'flex', flexDirection: 'column', gap: 24,
            }}>
              {/* CMS runs no dedupe on $report-data, so a second filing is silently accepted as a
                  new submission. Warn — never block: a deliberate re-file (CMS asked for a
                  correction) is legitimate, and the record carries no report_kind so we cannot
                  tell WHICH report was accepted. */}
              {!previewAbort && priorReports.length > 0 && (
                <Callout tone="moderate">
                  <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
                    <strong>
                      CMS has already accepted {priorReports.length === 1 ? 'a report' : `${priorReports.length} reports`} for
                      this patient
                    </strong>
                    {priorDate ? ` — most recently ${priorDate}.` : '.'}
                    {' '}CMS accepts duplicates silently, so this files another report rather than
                    replacing it. Continue only if you are deliberately re-filing.
                  </p>
                </Callout>
              )}

              {/* The bundle carries BP that was typed into the Withings app rather than measured.
                  It is here only because BP_ALLOW_MANUAL_ENTRY is on for this project; a project
                  enforcing the rule would refuse the submission outright. The operator is the last
                  person who can catch that, so it cannot be silent. */}
              {!previewAbort && manualBpCount > 0 && (
                <Callout tone="moderate">
                  <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
                    <strong>
                      {manualBpCount} of the blood-pressure readings in this bundle {manualBpCount === 1 ? 'was' : 'were'} entered by hand
                    </strong>
                    {' '}— typed into the Withings app rather than measured by the cuff. CMS does not
                    permit manual entry of blood pressure (RFA Appendix C), so this submission is
                    only possible because manual entry is enabled on this n8n project. It would be
                    refused on a project that enforces the rule.
                  </p>
                </Callout>
              )}

              {previewAbort ? (
                <>
                  <VerdictBanner
                    tone={requirementBanner?.tone || resultMeta?.tone || 'moderate'}
                    headline={requirementBanner?.headline || resultMeta?.headline || result.display}
                    resultCode={bannerCode}
                    /* WF37's result_display restates the headline for these codes — the
                       actionable detail is the issues list below. */
                    fhirDisplay={null}
                    /* SERVER FIRST. The static explainer is a generic description of the code;
                       `reason` is what WF37 found for THIS run — the reading count, and whether
                       the shortfall is real or the fetch failed. Those diverge: the generic BP
                       copy says "ask the patient to measure", which is the wrong instruction when
                       the truth is that we could not reach the device service at all. */
                    explainer={requirementBanner?.explainer || result.reason || resultMeta?.explainer}
                  />
                  {issues.length > 0 && (
                    <Callout tone="moderate">
                      <ul className="body2" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                        {issues.map((iss, i) => (
                          <li key={i} style={{ marginBottom: i < issues.length - 1 ? 6 : 0 }}>
                            {iss.detail || iss.code || JSON.stringify(iss)}
                          </li>
                        ))}
                      </ul>
                    </Callout>
                  )}
                </>
              ) : (
                /* Was a full-width info callout, stacked under the duplicate warning: two banners
                   filled the whole first screen before a single measure was visible. It restated
                   the section header directly below it ("Bundle contents (will be submitted)") and
                   the button beneath. Kept as a plain heading + one muted line — the heading role
                   and name are unchanged, so it still reads the same to assistive tech. */
                <div>
                  <h3 className="header4" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>
                    Ready to submit — review the payload
                  </h3>
                  <p className="detail1" style={{ margin: '2px 0 0', color: 'var(--wi-text-secondary)' }}>
                    Built from the latest data on file. Nothing has been sent to CMS yet.
                  </p>
                </div>
              )}

              <ElationLabCallout filled={filledSlugs} probe={result.elation_lab_probe} />

              {Number.isFinite(payload.observation_count) && (
                <Section label={submitBlocked ? 'Bundle contents (nothing will be sent)' : 'Bundle contents (will be submitted)'}>
                  <SubmittedPayload
                    count={payload.observation_count}
                    names={Array.isArray(payload.observations) ? payload.observations : []}
                    detail={Array.isArray(payload.observations_detail) ? payload.observations_detail : []}
                    effectiveDateTime={payload.effective_date_time}
                    reportKind={reportKind}
                    drafts={manualDrafts}
                    draftErrors={draftErrors}
                    onDraftChange={handleDraftChange}
                    blocked={submitBlocked}
                    requiredSlugs={Array.isArray(result.required_slugs) ? result.required_slugs : []}
                    recommendedSlugs={Array.isArray(result.recommended_slugs) ? result.recommended_slugs : []}
                    requiredWhy={result.required_why || {}}
                    editable
                  />
                </Section>
              )}

              {hasDrafts && (
                <Callout tone={manualDirty ? 'moderate' : 'info'}>
                  <p className="body2" style={{ margin: 0, color: manualDirty ? 'var(--wi-text-primary-moderate)' : 'var(--wi-text-primary-info)' }}>
                    {manualDirty
                      ? 'The table above still shows the bundle WITHOUT your entries. Re-check the bundle to see exactly what will be sent.'
                      /* The dangling clause here used to read as "weight is the one CMS won't take
                         patient-reported" — the exact inverse of Appendix C p56, which permits a
                         home-scale weight and is the whole reason the cuff rule is BP-only. "device"
                         was stale too: BP stopped being enterable on 2026-08-06. */
                      : 'On submit, these values are saved to the patient\'s record and sent to Medicare. Transcribe each one from its clinical source — a lab report for HbA1c and LDL, a measurement for waist. Weight is the exception: CMS accepts a patient-reported weight from a home scale. Height is not sent to CMS — it is stored and used to calculate BMI.'}
                  </p>
                </Callout>
              )}

              {result.result_code === 'required-measures-missing' && (
                <Section label="File without a required measure">
                  <Callout tone="bad">
                    <p className="detail1" style={{ margin: '0 0 8px', color: 'var(--wi-text-primary-bad)' }}>
                      Enter the missing values above — that is the fix. Override only when they
                      genuinely cannot be obtained in time: filing incomplete costs this patient
                      attainment on the missing measure, but missing the CMS deadline unaligns them
                      from the model entirely.
                      {deadlineDays != null && (
                        <> {deadlineDays >= 0
                          ? `${deadlineDays} day(s) left in the 60-day baseline window.`
                          : `The 60-day baseline window closed ${Math.abs(deadlineDays)} day(s) ago.`}</>
                      )}
                    </p>
                    {!overrideOpen ? (
                      <button
                        type="button"
                        className="detail1"
                        onClick={() => setOverrideOpen(true)}
                        style={{
                          border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                          color: 'var(--wi-action-primary)', textDecoration: 'underline',
                          fontFamily: 'var(--wi-font-family)',
                        }}
                      >Submit incomplete anyway…</button>
                    ) : (
                      <>
                        <label className="eyebrow" htmlFor="cms-override-reason" style={{ display: 'block', marginBottom: 4 }}>
                          Why can these measures not be obtained? (recorded on the submission)
                        </label>
                        <textarea
                          id="cms-override-reason"
                          className="call-notes-input"
                          rows={3}
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          placeholder="e.g. Quest cannot draw before the deadline; patient is homebound and mobile phlebotomy is booked for…"
                          style={{ width: '100%', resize: 'vertical' }}
                        />
                        <span className="detail1" style={{ color: 'var(--wi-text-placeholder)' }}>
                          {overrideReady
                            ? 'Confirm below to file incomplete — this reason is stored on the CMS submission record.'
                            : `${OVERRIDE_MIN_REASON - overrideReason.trim().length} more character(s) needed.`}
                        </span>
                      </>
                    )}
                  </Callout>
                </Section>
              )}

              {fetchErrors.length > 0 && (
                <Section label={`Context fetch warnings (${fetchErrors.length})`}>
                  <Callout tone="moderate">
                    <p className="detail1" style={{ margin: '0 0 6px', color: 'var(--wi-text-primary-moderate)' }}>
                      WF37 couldn&apos;t read one or more upstream sources before building the Bundle. You can still submit, but some observations may be missing.
                    </p>
                    <ul className="body2" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                      {fetchErrors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </Callout>
                </Section>
              )}

              {payload.fhir_bundle && (
                <Section label="FHIR Bundle (raw JSON)">
                  <button
                    type="button"
                    onClick={() => setShowRawJson(v => !v)}
                    aria-expanded={showRawJson}
                    aria-controls="cms-reporting-bundle-json"
                    className="detail1"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'transparent', border: '1px solid var(--wi-divider)',
                      cursor: 'pointer', padding: '4px 10px', borderRadius: 6,
                      color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)',
                      marginBottom: showRawJson ? 8 : 0,
                    }}
                  >
                    {showRawJson ? 'Hide raw JSON' : 'Show raw JSON'}
                  </button>
                  {showRawJson && (
                    <pre
                      id="cms-reporting-bundle-json"
                      style={{
                        margin: 0,
                        padding: 12,
                        background: 'var(--wi-background-primary)',
                        border: '1px solid var(--wi-divider)',
                        borderRadius: 8,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: 11,
                        lineHeight: 1.4,
                        color: 'var(--wi-text-primary)',
                        overflowX: 'auto',
                        maxHeight: 320,
                        overflowY: 'auto',
                        whiteSpace: 'pre',
                      }}
                    >
                      {JSON.stringify(payload.fhir_bundle, null, 2)}
                    </pre>
                  )}
                </Section>
              )}
            </div>
          )}

          {phase === 'error' && (
            <ErrorPanel message={error} />
          )}

          {phase === 'result' && (!result || !resultMeta) && (
            <div style={{ padding: '24px 0' }}>
              <p className="body2" style={{ margin: 0, color: 'var(--wi-status-bad)' }}>
                CMS responded but we couldn&apos;t parse the result.
              </p>
              <p className="detail1" style={{ margin: '8px 0 0', color: 'var(--wi-text-secondary)' }}>
                The Tier 4 proxy returned an empty or malformed body. Re-run, or check the n8n execution log for WF37. Raw response: <code>{JSON.stringify(result)}</code>
              </p>
            </div>
          )}

          {phase === 'result' && result && resultMeta && (
            <div style={{
              opacity: resultVisible ? 1 : 0,
              transition: 'opacity 200ms ease',
              // One rhythm for the whole panel. Without it the blocks butt up against each other
              // (a warning banner, a heading and a data card with no air between them read as one
              // dense slab), and each block had to carry its own margin to compensate.
              display: 'flex', flexDirection: 'column', gap: 24,
            }}>
              {/* Verdict banner */}
              <VerdictBanner
                tone={resultMeta.tone}
                headline={resultMeta.headline}
                resultCode={bannerCode}
                fhirDisplay={result.display}
                explainer={resultMeta.explainer}
              />

              {/* Where this verdict came from, when the caller timed out and we read the record */}
              {result.from_record && (
                <p className="detail1" style={{ margin: '10px 0 0', color: 'var(--wi-text-secondary)' }}>
                  Your browser&apos;s connection timed out while CMS was still processing, so this
                  was read from the submission record rather than the live response — which is why
                  the per-observation breakdown isn&apos;t shown.
                </p>
              )}

              {/* Reason — failure detail */}
              {result.reason && result.result_code !== 'success' && (
                <Section label="CMS detail">
                  <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>
                    {result.reason}
                  </p>
                </Section>
              )}

              {/* Issues — per-field validation messages (validation-error / incomplete-data) */}
              {issues.length > 0 && (
                <Section label={`Issues from CMS (${issues.length})`}>
                  <Callout tone="moderate">
                    <ul className="body2" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                      {issues.map((iss, i) => (
                        <li key={i} style={{ marginBottom: i < issues.length - 1 ? 6 : 0 }}>
                          {iss.code && (
                            <span
                              className="detail1"
                              style={{
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                background: 'var(--wi-background-secondary)',
                                padding: '1px 6px',
                                borderRadius: 4,
                                marginRight: 6,
                              }}
                            >
                              {iss.code}
                            </span>
                          )}
                          {iss.detail || iss.severity || JSON.stringify(iss)}
                        </li>
                      ))}
                    </ul>
                  </Callout>
                </Section>
              )}

              {/* CMS report id — copy affordance */}
              {result.cms_report_id && (
                <Section label="CMS report ID">
                  <ReportIdBlock
                    reportId={result.cms_report_id}
                    copied={copied === 'reportid'}
                    onCopy={handleCopyReportId}
                  />
                </Section>
              )}

              {/* What was sent */}
              {Number.isFinite(payload.observation_count) && (
                <Section label="Bundle contents">
                  <SubmittedPayload
                    count={payload.observation_count}
                    names={Array.isArray(payload.observations) ? payload.observations : []}
                    detail={Array.isArray(payload.observations_detail) ? payload.observations_detail : []}
                    effectiveDateTime={payload.effective_date_time}
                    reportKind={reportKind}
                  />
                </Section>
              )}

              {/* Manually entered values that were written to the patient record */}
              {manualRecorded.length > 0 && (
                <Section label={`Saved to the patient record (${manualRecorded.length})`}>
                  <div style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: 12 }}>
                    <ul className="body2" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary)' }}>
                      {manualRecorded.map((r) => (
                        <li key={r.slug}>
                          {MANUAL_OBSERVATIONS[r.slug]?.label || r.slug}
                          <span style={{ color: 'var(--wi-text-secondary)' }}>
                            {/* Height is not a measure CMS receives — calling it one here would
                                contradict the row above, which explains it is a BMI input. */}
                            {r.slug === 'height' ? ' — used to calculate BMI' : ' — patient measures'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Section>
              )}

              {/* Upstream-fetch warnings (one or more of the 5 platform-API reads
                  inside WF37 failed). The submission still went through with the
                  data it could gather, but the bundle may be incomplete. */}
              {fetchErrors.length > 0 && (
                <Section label={`Context fetch warnings (${fetchErrors.length})`}>
                  <Callout tone="moderate">
                    <p className="detail1" style={{ margin: '0 0 6px', color: 'var(--wi-text-primary-moderate)' }}>
                      WF37 couldn&apos;t read one or more upstream sources before building the FHIR Bundle. The submission proceeded with what it had, but observations may be missing.
                    </p>
                    <ul className="body2" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                      {fetchErrors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </Callout>
                </Section>
              )}

              {/* Side-effects: did the task actually close, and did the audit row take the verdict? */}
              {(sideEffects.task_completed || sideEffects.task_completion_failed || sideEffects.audit_row_updated === false) && (
                <Section label="System changes">
                  <div style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: 12, display: 'grid', gap: 8 }}>
                    {sideEffects.task_completed && (
                      <p className="body2" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--wi-text-primary)' }}>
                        <CheckIcon size={14} />
                        <span><strong>{reportKindLabel.label}</strong> marked complete</span>
                      </p>
                    )}
                    {/* CMS accepted the report but the close failed — the report IS filed; only the
                        task is stale. Saying nothing would leave it open with no explanation.
                        ⚠️ Do NOT advise closing it by hand: WF17 REFUSES a caller-driven completion
                        of any rpt_*_submission unless completion_source === 'system', so that advice
                        was unactionable by design. And this only fires when a close was genuinely
                        attempted — WF37's `eligible` reuses the gate's own verdict, so a submission
                        with no open task to satisfy says nothing at all rather than this. */}
                    {sideEffects.task_completion_failed && (
                      <p className="body2" style={{ margin: 0, color: 'var(--wi-status-moderate)' }}>
                        CMS accepted the report, but the <strong>{reportKindLabel.label}</strong> task could not be closed automatically. The submission stands and CMS has the report — the task will just stay open on the board. It cannot be completed by hand, so flag it to platform-eng.
                      </p>
                    )}
                    {sideEffects.audit_row_updated === false && (
                      <p className="body2" style={{ margin: 0, color: 'var(--wi-status-moderate)' }}>
                        The submission record could not be updated with this result. The report reached CMS; the audit row may show it as still pending.
                      </p>
                    )}
                  </div>
                </Section>
              )}

              {/* Timestamp */}
              {checkedAtLabel && (
                <p className="detail1" style={{ margin: '20px 0 0', color: 'var(--wi-text-placeholder)' }}>
                  Submitted {checkedAtLabel}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 24px',
          borderTop: '1px solid var(--wi-divider)',
          flexShrink: 0,
        }}>
          <div>
            {phase === 'result' && result && (
              <button
                onClick={handleCopyVerdict}
                aria-label="Copy verdict to clipboard"
                title="Copy a one-line summary to paste into a chart note or chat"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '6px 8px', borderRadius: 6,
                  color: 'var(--wi-text-secondary)',
                  fontFamily: 'var(--wi-font-family)',
                }}
                className="detail1"
              >
                <CopyIcon size={14} />
                {copied === 'verdict' ? 'Copied' : 'Copy result'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            {(phase === 'preview-loading' || phase === 'submit-loading') && (
              <button className="btn-ok" onClick={onClose}>Cancel</button>
            )}
            {phase === 'preview' && (
              <>
                <button className="btn-ok" onClick={onClose}>Cancel</button>
                {hasDrafts && (
                  <button
                    className={manualDirty ? 'btn-primary' : 'btn-ok'}
                    onClick={handleRunPreview}
                    disabled={hasDraftErrors || compositePending}
                    title={hasDraftErrors ? 'Fix the highlighted values first'
                      : compositePending ? 'Finish the height you started — feet and inches'
                        : 'Rebuild the bundle with your entered values (nothing is saved or sent)'}
                  >
                    Re-check bundle
                  </button>
                )}
                <button
                  className="btn-primary"
                  onClick={handleConfirmSubmit}
                  disabled={hasDraftErrors || manualDirty || confirmIsNoop || compositePending}
                  title={hasDraftErrors
                    ? 'Fix the highlighted values first'
                    : compositePending ? 'Finish the height you started — feet and inches'
                      : confirmIsNoop ? PARSE_ABORT_TOOLTIP[result.result_code]
                      : submitBlocked ? 'CMS submission is blocked, but the values you entered above will still be saved to the record'
                        : manualDirty ? 'Re-check the bundle so you can see what will be sent' : undefined}
                >
                  {blockedSubmitLabel || 'Confirm and submit to CMS'}
                </button>
              </>
            )}
            {phase === 'error' && (
              <>
                <button className="btn-ok" onClick={onClose}>Close</button>
                <button className="btn-primary" onClick={handleRunPreview}>Retry</button>
              </>
            )}
            {phase === 'result' && (
              <button className="btn-primary" onClick={handleDone}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

/* ══════════════════════════════════════════════════════════════════════
 * Subcomponents (mirror CmsEligibilityModal for visual consistency)
 * ════════════════════════════════════════════════════════════════════ */

function LoadingPanel({ title, stepIndex, steps, footnote }) {
  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <Spinner />
        <p className="header4" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>{title}</p>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((label, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <StepDot state={done ? 'done' : active ? 'active' : 'pending'} />
              <span
                className="body2"
                style={{
                  color: done || active ? 'var(--wi-text-primary)' : 'var(--wi-text-placeholder)',
                  fontWeight: active ? 500 : 400,
                }}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
      {footnote && (
        <p className="detail1" style={{ margin: '20px 0 0', color: 'var(--wi-text-placeholder)' }}>
          {footnote}
        </p>
      )}
    </div>
  );
}

function StepDot({ state }) {
  if (state === 'done') {
    return (
      <span
        aria-hidden
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: 'var(--wi-status-good)',
          color: 'var(--wi-on-action-primary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span
        aria-hidden
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: 'var(--wi-status-info-10)',
          border: '2px solid var(--wi-action-primary)',
          flexShrink: 0,
          animation: 'wi-pulse 1.4s ease-in-out infinite',
        }}
      />
    );
  }
  return (
    <span
      aria-hidden
      style={{
        width: 16, height: 16, borderRadius: '50%',
        background: 'transparent',
        border: '2px solid var(--wi-divider)',
        flexShrink: 0,
      }}
    />
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        width: 22, height: 22, flexShrink: 0,
        border: '2.5px solid var(--wi-action-secondary)',
        borderTopColor: 'var(--wi-action-primary)',
        borderRadius: '50%',
        animation: 'wi-spin 0.85s linear infinite',
      }}
    />
  );
}

function ErrorPanel({ message }) {
  return (
    <div>
      <VerdictBanner
        tone="bad"
        headline="We couldn't reach CMS"
        resultCode={null}
        fhirDisplay={null}
        explainer="The CMS Reporting service didn't respond. This is usually transient infrastructure — try again in a moment. If it persists, check the n8n execution log for WF37 and contact platform-eng."
      />
      {message && (
        <Section label="Detail">
          <p className="body2" style={{ margin: 0, color: 'var(--wi-text-secondary)' }}>{message}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    // Spacing comes from the parent's gap, not from here — otherwise the two compound and the
    // rhythm depends on which container a Section happens to sit in.
    <div>
      <p className="eyebrow" style={{ margin: '0 0 10px', color: 'var(--wi-text-secondary)' }}>{label}</p>
      {children}
    </div>
  );
}

function Callout({ tone, children }) {
  const bg = {
    good:     'var(--wi-background-good)',
    moderate: 'var(--wi-background-moderate)',
    bad:      'var(--wi-background-bad)',
    info:     'var(--wi-background-info)',
  }[tone] || 'var(--wi-background-primary)';
  return (
    <div style={{ background: bg, borderRadius: 8, padding: 12 }}>
      {children}
    </div>
  );
}

function ReportIdBlock({ reportId, copied, onCopy }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px',
        background: 'var(--wi-background-primary)',
        borderRadius: 8,
        border: '1px solid var(--wi-divider)',
      }}
    >
      <span
        className="body2"
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          letterSpacing: '0.04em',
          color: 'var(--wi-text-primary)',
          flex: 1,
        }}
      >
        {reportId}
      </span>
      <button
        onClick={onCopy}
        aria-label="Copy report id"
        title="Copy CMS report id"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 6, borderRadius: 6, display: 'inline-flex', alignItems: 'center',
          color: copied ? 'var(--wi-status-good)' : 'var(--wi-text-secondary)',
        }}
      >
        {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      </button>
    </div>
  );
}

// WF37 names the patient's day and zone for the BP readings; an older WF37 doesn't, and then nothing claims "patient's time".
function FiledBpReadingsRow({ row }) {
  const header = bpReadingsHeader(row);
  return (
    <tr style={{ borderTop: '1px dashed var(--wi-divider)' }}>
      <td colSpan={5} style={{ ...obsCellStyle, whiteSpace: 'normal', paddingTop: 6, paddingBottom: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p className="detail1" style={{ margin: 0, color: 'var(--wi-text-secondary)' }}>
            {header.text}
            {row.readings.every(r => r.manual) ? '. All hand-entered.'
              : row.readings.some(r => r.manual) ? '. Some hand-entered (marked).' : '.'}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
            {row.readings.map((r, i) => {
              const when = bpReadingWhen(r, row, header.zoned);
              return (
                <span key={r.at || i} className="detail1" style={{ color: 'var(--wi-text-primary)' }}>
                  <strong>{r.sys}/{r.dia}</strong>
                  {when ? <span style={{ color: 'var(--wi-text-placeholder)' }}> {when}</span> : null}
                  {r.manual && !row.readings.every(x => x.manual)
                    ? <span style={{ color: 'var(--wi-text-placeholder)' }}> · typed</span> : null}
                </span>
              );
            })}
          </div>
        </div>
      </td>
    </tr>
  );
}

/* SubmittedPayload — observation table when WF37 emits observations_detail[]
   (post-2026-05-12). Falls back to the chip list for older WF37 responses. */
export function SubmittedPayload({
  count, names, detail, effectiveDateTime, reportKind,
  drafts = {}, draftErrors = {}, onDraftChange, editable = false, blocked = false,
  requiredSlugs = /** @type {string[]} */ ([]),
  recommendedSlugs = /** @type {string[]} */ ([]),
  requiredWhy = /** @type {Record<string, string>} */ ({}),
}) {
  // "missing" alone reads the same for a measure CMS demands and one it never asked for, which
  // is how a beneficiary loses attainment while the screen looks merely incomplete.
  const isRequired = (slug) => requiredSlugs.includes(slug);
  // Which measures CMS demands is diagnosis-dependent (Payment Amounts Tables 3/4 — eGFR/uACR bind
  // only for CKM + diabetes/CKD), so it cannot be read off the measure name and has to come from
  // WF37's gate. 'recommended' is the honest third state: the care plan could not be read, so a
  // diagnosis-conditional measure degrades to advisory rather than inventing a requirement.
  const requirementOf = (slug) => (isRequired(slug) ? 'required'
    : recommendedSlugs.includes(slug) ? 'recommended' : 'optional');
  // A recorded value rendered read-only, so a mistyped one could not be corrected from here —
  // the operator had to go and change measure_state by other means, or file it wrong. Editing
  // resets on every re-check, which is what you want: after re-previewing, the new value is the
  // fact again rather than a field left open.
  const [editing, setEditing] = useState(() => new Set());
  const toggleEdit = (slug) => setEditing((prev) => {
    const next = new Set(prev);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    return next;
  });
  const hasDetail = Array.isArray(detail) && detail.length > 0;
  const presentCount = hasDetail ? detail.filter(d => d.present).length : count;
  const missingCount = hasDetail ? detail.filter(d => !d.present).length : 0;
  const kindLabel = REPORT_KIND_LABELS[reportKind]?.label || 'CMS report';
  const typedFor = (slug) => (drafts[slug]?.value || '').trim() !== '';
  // The number that decides whether this submission can go at all — a typed value already
  // counts, so the count follows what WILL be sent rather than what the bundle arrived with.
  const missingRequired = hasDetail
    ? detail.filter((d) => !d.present && !typedFor(d.slug) && isRequired(d.slug)).length : 0;

  return (
    <div style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: 12, border: '1px solid var(--wi-divider)' }}>
      <p className="body2" style={{ margin: '0 0 10px', color: 'var(--wi-text-primary)' }}>
        {/* "ready to submit" is a promise. When WF37 has already refused, the bundle is a
            preview of what WOULD go — saying otherwise contradicts the banner directly above. */}
        {/* MEASURES, not observations: BP is one row here and N Observations on the wire. */}
        <strong>{presentCount}</strong> measure{presentCount === 1 ? '' : 's'}
        {blocked ? ' in the bundle' : ' ready to submit'}
        {effectiveDateTime ? <> for <strong>{effectiveDateTime}</strong></> : null}
        {missingCount > 0 ? <> · <span style={{ color: 'var(--wi-text-placeholder)' }}>
          {missingCount} missing from {kindLabel.toLowerCase()} superset
          {missingRequired > 0 ? <>, <strong style={{ color: 'var(--wi-text-primary-bad)' }}>{missingRequired} required</strong></> : null}
        </span></> : null}
        .
      </p>

      {hasDetail ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--wi-font-family)' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th className="detail1" style={obsHeaderStyle}>Measure</th>
                {/* The LOINC used to sit here. It is the CMS wire code — audit detail a coordinator
                    never acts on — while whether CMS *demands* the measure is the one thing on this
                    row that decides if the submission can go. The code is still on the measure's
                    tooltip and in the raw bundle below. */}
                <th className="detail1" style={obsHeaderStyle}>Requirement</th>
                <th className="detail1" style={{ ...obsHeaderStyle, textAlign: 'right' }}>Value</th>
                <th className="detail1" style={obsHeaderStyle}>Unit</th>
                <th className="detail1" style={obsHeaderStyle}>Source</th>
              </tr>
            </thead>
            <tbody>
              {detail.map((row) => {
                const prov = provenanceOf(drafts[row.slug]);
                // A row the operator has typed into is no longer absent — it WILL be sent, and it
                // carries a `manual` source chip saying so. Keying the emphasis on row.present
                // (what the BUNDLE arrived with) left a filled-in, about-to-be-submitted measure
                // rendered as if it were still missing.
                const willSend = row.present || typedFor(row.slug);
                return (
                <React.Fragment key={row.slug}>
                {/* No row-level opacity: it dimmed the <input> too, so an editable field looked
                    disabled. Absence is carried by the empty value and the '—' source instead. */}
                <tr style={{ borderTop: '1px solid var(--wi-divider)' }}>
                  <td
                    className="body2"
                    title={row.loinc ? `LOINC ${row.loinc}` : undefined}
                    style={{ ...obsCellStyle, color: willSend ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)' }}
                  >{row.display}</td>
                  {/* Its own column, not a chip beside the name: a row the operator can type into
                      renders an input in the VALUE cell, so a marker there was invisible for exactly
                      the measures they act on — HbA1c, LDL and waist all looked alike. */}
                  <td style={obsCellStyle}>
                    <RequirementTag kind={requirementOf(row.slug)} why={requiredWhy[row.slug]} />
                  </td>
                  <td
                    className="body2"
                    style={{
                      ...obsCellStyle,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: willSend ? 500 : 400,
                      color: willSend ? 'var(--wi-text-primary)' : 'var(--wi-text-placeholder)',
                    }}
                  >
                    {(editable && metaFor(row.slug) && (!row.present || editing.has(row.slug)))
                      ? (
                        <ManualValueCell
                          slug={row.slug}
                          drafts={drafts}
                          draftErrors={draftErrors}
                          onDraftChange={onDraftChange}
                        />
                      )
                      : row.present
                        ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                            {/* CMS requires metric on the wire (every IG bundle example: kg / cm),
                                but the care team reads lb / in — on the Elation chart and now here.
                                So US customary leads and the metric that goes to CMS rides behind
                                it, rather than making a coordinator convert in their head (which
                                is how 125 lb was typed into a kg field on 2026-08-27). */}
                            {hasUsEntryUnit(row.slug) && Number(row.value) > 0
                              ? metricToEntry(row.slug, Number(row.value))
                              : row.value}
                            {hasUsEntryUnit(row.slug) && Number(row.value) > 0 && (
                              <span className="detail1" style={{ color: 'var(--wi-text-placeholder)' }}>
                                ({row.value} {row.unit})
                              </span>
                            )}
                            {editable && metaFor(row.slug) && (
                              <button
                                type="button"
                                onClick={() => toggleEdit(row.slug)}
                                aria-label={`Change ${metaFor(row.slug).label}`}
                                className="detail1"
                                style={{
                                  border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                                  color: 'var(--wi-action-primary)', textDecoration: 'underline',
                                  fontFamily: 'var(--wi-font-family)',
                                }}
                              >Change</button>
                            )}
                          </span>
                        )
                        : isRequired(row.slug)
                          ? <span style={{ color: 'var(--wi-text-primary-bad)' }}>— not available</span>
                          : '— not available'}
                  </td>
                  {/* The unit must label what the operator is looking at — including an EMPTY row
                      they are about to type into, which is where "type inches, labelled cm" came
                      from. */}
                  <td className="detail1" style={{ ...obsCellStyle, color: 'var(--wi-text-secondary)' }}>
                    {hasUsEntryUnit(row.slug) ? entryUnitFor(row.slug) : row.unit}
                  </td>
                  <td className="detail1" style={obsCellStyle}>
                    {row.source || typedFor(row.slug) || prov ? (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: prov ? 'var(--wi-background-info)' : sourceTone(row.source, typedFor(row.slug)).bg,
                          color: prov ? 'var(--wi-text-primary-info)' : sourceTone(row.source, typedFor(row.slug)).fg,
                          border: '1px solid var(--wi-divider)',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                          fontSize: 10,
                        }}
                      >
                        {prov ? 'Elation lab' : sourceLabel(row.source, typedFor(row.slug))}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--wi-text-placeholder)' }}>—</span>
                    )}
                  </td>
                </tr>
                {/* CMS averages the readings we send, so the table shows the average — and this
                    row shows what it is made of. "155/88 x3" read as three identical readings when
                    the three sent were 155/88, 150/88 and 160/90: the spread was invisible, and so
                    would a crisis-level outlier among them have been. */}
                {row.slug === 'systolic_bp' && Array.isArray(row.readings) && (row.readings.length > 1 || (row.day && row.readings.length === 1)) && (
                  <FiledBpReadingsRow row={row} />
                )}
                {/* BMI is derived, so it has no input of its own — the fix is the height it is
                    missing. Naming the reason and offering the remedy in the same place is the
                    difference between a blank cell and something the operator can act on. */}
                {row.slug === 'bmi' && !row.present && (
                  <tr style={{ borderTop: '1px dashed var(--wi-divider)' }}>
                    {/* obsCellStyle is nowrap (it keeps the measure columns on one line) and
                        white-space INHERITS — without this the sentence below never wraps and
                        the whole table grows a horizontal scrollbar. Same reason the error span
                        in ManualValueCell sets it. */}
                    <td colSpan={5} style={{ ...obsCellStyle, whiteSpace: 'normal', paddingTop: 6, paddingBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                        <p className="detail1" style={{ margin: 0, flex: '1 1 260px', color: 'var(--wi-text-secondary)' }}>
                          {omission(row.reason).text
                            || 'BMI was not calculated. It is derived from the weight being sent and the patient’s height.'}
                        </p>
                        {editable && omission(row.reason).offerHeight && metaFor('height') && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>
                              {/* Bare "Height": the cell's own boxes carry the units, and the
                                  visible text stays contained by each accessible name
                                  ("Enter Height feet" / "Enter Height inches") — WCAG 2.5.3. */}
                              {metaFor('height').label}
                            </span>
                            <ManualValueCell
                              slug="height"
                              drafts={drafts}
                              draftErrors={draftErrors}
                              onDraftChange={onDraftChange}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        names.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {names.map((slug) => (
              <span
                key={slug}
                className="detail1"
                style={{
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'var(--wi-background-secondary)',
                  color: 'var(--wi-text-secondary)',
                  border: '1px solid var(--wi-divider)',
                }}
              >
                {OBSERVATION_LABELS[slug] || slug}
              </span>
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* A missing row becomes an entry field: value (+ diastolic on the BP row) + the date the
   measurement was actually taken, which becomes the Observation's effectiveDateTime. */
function ManualValueCell({ slug, drafts, draftErrors, onDraftChange }) {
  const slugs = [slug, ...(PAIRED_SLUG[slug] ? [PAIRED_SLUG[slug]] : [])];
  const dateError = manualDateError(slug, drafts[slug]?.collectedAt);
  const prov = provenanceOf(drafts[slug]);
  // One box of a composite filled, the other not — no value, no error, so this is the only thing
  // on screen saying why. It also disables Confirm (compositeIncomplete).
  const halfTyped = (s) => compositeEntryFor(s) === 'ft-in'
    && ((drafts[s]?.valueFt ?? '') !== '' || (drafts[s]?.valueIn ?? '') !== '')
    && !totalInchesOf(drafts[s]?.valueFt, drafts[s]?.valueIn);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
      {slugs.map((s) => {
        const meta = MANUAL_OBSERVATIONS[s];
        const error = draftErrors[s];
        // The error and the half-typed hint are the only places the reason is stated, so every box
        // must point at them — not just at the provenance line.
        const describedBy = [
          error ? `err-${s}` : null,
          halfTyped(s) ? `hint-${s}` : null,
          s === slug && prov ? `prov-${slug}` : null,
        ].filter(Boolean).join(' ') || undefined;
        const boxStyle = {
          textAlign: 'right',
          padding: '3px 6px',
          borderRadius: 6,
          border: `1px solid ${error ? 'var(--wi-status-bad)' : 'var(--wi-divider)'}`,
          background: 'var(--wi-background-secondary)',
          color: 'var(--wi-text-primary)',
          fontFamily: 'var(--wi-font-family)',
          fontVariantNumeric: 'tabular-nums',
        };
        // The pair writes ONE scalar (total inches), which every validator and the payload already
        // handle; the raw boxes ride alongside so the box under the cursor is never re-derived.
        if (compositeEntryFor(s) === 'ft-in') {
          const setBox = (which) => (e) => {
            const raw = e.target.value;
            onDraftChange(s, (before) => {
              const ft = which === 'ft' ? raw : (before.valueFt ?? '');
              const inch = which === 'ft' ? (before.valueIn ?? '') : raw;
              return { valueFt: ft, valueIn: inch, value: totalInchesOf(ft, inch) };
            });
          };
          // Feet bounds follow the metric guard rather than repeating it: 48-86 in is 4-7 ft.
          const bounds = entryBounds(s) ?? { min: 48, max: 86 };
          return (
            <span key={s} role="group" aria-label={meta.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {[
                { which: 'ft', word: 'feet', value: drafts[s]?.valueFt ?? '', min: feetInchesOf(bounds.min).ft, max: feetInchesOf(bounds.max).ft },
                { which: 'in', word: 'inches', value: drafts[s]?.valueIn ?? '', min: 0, max: 11 },
              ].map((box) => (
                // "feet" / "inches" is visible AND inside the accessible name (WCAG 2.5.3), spelled
                // out rather than abbreviated so speech input has something to say.
                <label key={box.which} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    inputMode="numeric"
                    step={1}
                    min={box.min}
                    max={box.max}
                    value={box.value}
                    aria-label={`Enter ${meta.label} ${box.word}`}
                    aria-describedby={describedBy}
                    aria-invalid={error ? 'true' : undefined}
                    onChange={setBox(box.which)}
                    // A focused number input eats the wheel event: scrolling the table behind it
                    // would silently retype the height.
                    onWheel={(e) => e.currentTarget.blur()}
                    className="body2"
                    style={{ ...boxStyle, width: 52 }}
                  />
                  <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>{box.word}</span>
                </label>
              ))}
            </span>
          );
        }
        return (
          <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
            {slugs.length > 1 && (
              <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>{meta.label}</span>
            )}
            <input
              type="number"
              inputMode="decimal"
              step={(entryBounds(s) ?? meta).step}
              min={(entryBounds(s) ?? meta).min}
              max={(entryBounds(s) ?? meta).max}
              value={drafts[s]?.value ?? ''}
              aria-label={`Enter ${meta.label} in ${entryUnitFor(s)}`}
              aria-describedby={describedBy}
              aria-invalid={error ? 'true' : undefined}
              placeholder="enter"
              onChange={(e) => onDraftChange(s, { value: e.target.value })}
              className="body2"
              style={{ ...boxStyle, width: 84 }}
            />
          </label>
        );
      })}
      {/* The operator types US customary, so the echo shows the metric value the entry becomes — a
          decimal slip is visible before submission rather than after. It must not say "to CMS" for
          a measure CMS never receives: height is submitted:false and the row above it says so. */}
      {hasUsEntryUnit(slug) && Number(drafts[slug]?.value) > 0 && (
        <span className="detail1" style={{ color: 'var(--wi-text-placeholder)' }}>
          {metaFor(slug).submitted ? '→' : '='} {entryToMetric(slug, Number(drafts[slug].value))} {metaFor(slug).unit}
          {metaFor(slug).submitted ? ' to CMS' : ' used for BMI'}
        </span>
      )}
      {/* Reading the blank box as 0 would file a plausible wrong height and KEEP it — WF37 writes
          it to measure_state and reuses it for every future submission. */}
      {halfTyped(slug) && (
        <span id={`hint-${slug}`} aria-live="polite" className="detail1" style={{ color: 'var(--wi-text-primary-bad)', textAlign: 'right', whiteSpace: 'normal', maxWidth: 190 }}>
          Enter whole feet and inches — 0 inches for an exact foot.
        </span>
      )}
      {/* Says which DATE it is: WF37 falls back to the grid's resulted_date when the report
          carries no collection date, and calling that "collected" would misstate the one field
          the CMS validity window is measured from. Editing the value or the date drops the line
          (and the provenance from the payload) — see provenanceOf. */}
      {prov && (
        <span id={`prov-${slug}`} className="detail1" style={{ color: 'var(--wi-text-primary-info)', textAlign: 'right', whiteSpace: 'normal', maxWidth: 190 }}>
          from Elation lab report · {prov.dateSource === 'resulted' ? 'resulted' : 'collected'} {prov.collectedAt}
        </span>
      )}
      {(drafts[slug]?.value || '').trim() !== '' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>taken</span>
          <input
            type="date"
            value={drafts[slug]?.collectedAt ?? ''}
            aria-label={`Date ${MANUAL_OBSERVATIONS[slug].label} was measured`}
            aria-invalid={dateError ? 'true' : undefined}
            onChange={(e) => onDraftChange(slug, { collectedAt: e.target.value })}
            className="detail1"
            style={{
              padding: '2px 6px',
              borderRadius: 6,
              border: `1px solid ${dateError ? 'var(--wi-status-bad)' : 'var(--wi-divider)'}`,
              background: 'var(--wi-background-secondary)',
              color: 'var(--wi-text-secondary)',
              fontFamily: 'var(--wi-font-family)',
            }}
          />
        </label>
      )}
      {slugs.filter((s) => draftErrors[s]).map((s) => (
        <span key={s} id={`err-${s}`} className="detail1" style={{ color: 'var(--wi-text-primary-bad)', textAlign: 'right', whiteSpace: 'normal', maxWidth: 190 }}>
          {draftErrors[s]}
        </span>
      ))}
    </div>
  );
}

/**
 * Whether CMS demands this measure for THIS beneficiary and THIS report kind.
 *
 * Optional is deliberately quiet rather than chipped — the operator is scanning for what they must
 * act on, and tagging every row equally would hide the two that block the filing.
 */
function RequirementTag({ kind, why }) {
  if (kind === 'optional') {
    return <span className="detail1" style={{ color: 'var(--wi-text-placeholder)' }} title="Not required by CMS for this report — send it if you have it">optional</span>;
  }
  const required = kind === 'required';
  return (
    <span
      className="detail1"
      title={why || (required
        ? 'Required by CMS for this report — the submission is blocked without it'
        : 'Advisory: the care plan could not be read, so we cannot tell whether CMS requires this one')}
      style={{
        padding: '1px 6px',
        borderRadius: 999,
        fontSize: 10,
        whiteSpace: 'nowrap',
        // The designed text-on-tint pair, not a --wi-status-* fill used as text (app.md).
        background: required ? 'var(--wi-background-bad)' : 'var(--wi-background-moderate)',
        color: required ? 'var(--wi-text-primary-bad)' : 'var(--wi-text-primary-moderate)',
      }}
    >{required ? 'Required' : 'Recommended'}</span>
  );
}

export function ElationLabCallout({ filled, probe }) {
  if (!probe || (probe.status === 'skipped' && probe.reason === 'not_needed')) return null;
  // Attributable refusals only — a refused code for a measure nobody is being asked for is noise.
  // A slug whose field currently HOLDS a value is dropped: "in the chart but unusable" printed
  // beside a filled input contradicts it. Keyed on the form, not on WF37's proposal — the
  // proposal is re-sent on every preview, so a cleared prefill would suppress the refusal
  // forever, exactly when the operator most needs to know the chart has an unusable result.
  const onScreen = filled instanceof Set ? filled : new Set();
  const refused = (Array.isArray(probe.unusable) ? probe.unusable : [])
    .filter((u) => u?.slug && u.reason !== 'foreign_patient' && !onScreen.has(u.slug));
  const UNUSABLE_REASON = {
    race_based_equation: 'reported with the retired race-based equation',
    different_analyte: 'a cystatin-C result, not the creatinine-based measure',
    wrong_specimen: 'a 24-hour-urine result, not the spot ratio',
    unit_mismatch: 'reported in a unit we do not convert',
    // Two different instructions. `not_calculable` = the lab returned a result, classified it
    // normal, and could not put a number on it (Quest does this when the urine albumin is below
    // the detection limit, so the albumin/creatinine ratio is uncomputable). Saying only "not a
    // numeric result" there reads like a data fault and invites re-ordering a normal test. The
    // claim is attributed to the lab, never asserted by us, and neither is submittable.
    not_calculable: 'reported as normal with no numeric value — the lab could not calculate one',
    non_numeric: 'not a numeric result — check the chart',
    out_of_range: 'outside the plausible range',
    stale: 'older than the 1-year validity window',
    undated: 'carries no collection date',
    conflicting_same_date: 'two different results on the same date — check the chart',
  };
  const NOT_CONSULTED = {
    budget: 'the preview was already close to its time limit',
    no_ehr_patient_id: 'this patient has no Elation chart id on file',
    other_abort: 'the submission was already blocked for another reason',
    unreadable: 'the chart could not be read',
  };
  const foreign = (Array.isArray(probe.unusable) ? probe.unusable : []).some((u) => u?.reason === 'foreign_patient');
  // Two different failures, and telling them apart matters: 'skipped' means we never opened the
  // chart (retrying may work), 'degraded' means we read it and threw the answer away.
  const skipped = probe.status === 'skipped';
  // A timeout got no answer at all, so retrying is the right next move; "could not be read"
  // implies an answer we could not use, which is a different instruction.
  const timedOut = probe.reason === 'timeout';
  const readButDiscarded = foreign
    ? 'the chart response did not match this patient'
    : probe.truncated ? 'the chart has more lab reports than one page, so we could not prove which result is newest'
      : timedOut ? 'it did not answer in time'
        : 'the chart could not be read';

  if (skipped || probe.status === 'degraded') {
    return (
      <Callout tone="moderate">
        <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
          {skipped
            ? <>The Elation chart was <strong>not</strong> consulted for the missing labs
              {NOT_CONSULTED[probe.reason] ? <> — {NOT_CONSULTED[probe.reason]}</> : null}.</>
            : timedOut
              ? <>The Elation chart was <strong>not</strong> read — {readButDiscarded}.</>
              : <>The Elation chart <strong>was</strong> read, but nothing from it could be used — {readButDiscarded}.</>}
          {' '}Enter the values by hand, or re-check to try again.
        </p>
      </Callout>
    );
  }
  // The chart WAS read and had nothing usable for anything. Nothing was prefilled, so this cannot
  // contradict a field — and it is the only place the operator learns Elation has already been
  // checked, which is what stops a wasted chart hunt.
  const notFound = (Array.isArray(probe.not_found) ? probe.not_found : []).filter(Boolean);
  if (refused.length === 0 && probe.status === 'ok' && notFound.length > 0
      && !(Array.isArray(probe.matched) && probe.matched.length > 0)) {
    return (
      <Callout tone="moderate">
        <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
          The Elation chart <strong>was</strong> checked — no usable result for{' '}
          {notFound.map((slug) => metaFor(slug)?.label || slug).join(', ')}. Enter the values by hand.
        </p>
      </Callout>
    );
  }
  // Otherwise silent: the value is in its field and the banner names the state, so a third box
  // repeating either read as the screen contradicting itself (PROD 28543097).
  if (refused.length === 0) return null;
  return (
    <Callout tone="info">
      <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-info)' }}>
        {/* Naming the reason is the point: "nothing found" would send them to order a test on file. */}
        In the chart but unusable: {refused.map(({ slug, reason }, i) => (
          <React.Fragment key={`${slug}-${reason}`}>
            {i > 0 ? ', ' : ''}<strong>{metaFor(slug)?.label || slug}</strong> ({UNUSABLE_REASON[reason] || reason})
          </React.Fragment>
        ))}.
      </p>
    </Callout>
  );
}

function sourceLabel(source, typed) {
  if (typed && !source) return 'manual';
  if (source === 'manual') return 'manual';
  if (source === 'baseline_value') return 'baseline';
  // BP is fetched from the cuff readings at submit time, not read from measure_state — the whole
  // point of the >=3-readings rule is that CMS gets the individual transmissions.
  if (source === 'device_readings') return 'cuff';
  // A lab that exists only in lab_result is neither a baseline nor a latest measure_state value.
  // This map falls through to 'latest', so an unhandled source silently states a false provenance.
  if (source === 'lab_result') return 'lab';
  // Machine-parsed from the chart into `latest_value`, indistinguishable from a typed value without
  // this label — on the screen where the operator vouches for the filing. Same word as the callout.
  if (source === 'elation_import') return 'chart';
  // BP that reached the bundle only because the manual-entry rule was relaxed. Calling it 'cuff'
  // would assert the one thing RFA Appendix C regulates about this measure.
  if (source === 'patient_entered') return 'typed';
  // BMI is computed from the weight in this very bundle (RFA Appendix C p56). It is never stored,
  // so 'latest' — "the patient's latest stored value" — states a provenance it cannot have, on a
  // Medicare review screen.
  if (source === 'derived') return 'derived';
  // The weight CMS receives is now the freshest reading inside its 15-day validity window, not
  // whatever measure_state happened to hold — a stored value carries no date we can check, and
  // baseline_value carries none at all.
  if (source === 'device') return 'device';
  return 'latest';
}

function sourceTone(source, typed) {
  if (source === 'manual' || (typed && !source)) {
    return { bg: 'var(--wi-background-moderate)', fg: 'var(--wi-text-primary-moderate)' };
  }
  if (source === 'patient_entered') {
    return { bg: 'var(--wi-background-moderate)', fg: 'var(--wi-text-primary-moderate)' };
  }
  // Machine-parsed out of the chart and never read by a clinician — the same "look at this one"
  // weight as a hand-typed value, not the neutral tone a stored device reading gets.
  if (source === 'elation_import') {
    return { bg: 'var(--wi-background-moderate)', fg: 'var(--wi-text-primary-moderate)' };
  }
  if (source === 'baseline_value' || source === 'device_readings' || source === 'derived' || source === 'device') {
    return { bg: 'var(--wi-background-info)', fg: 'var(--wi-text-primary-info)' };
  }
  return { bg: 'var(--wi-background-secondary)', fg: 'var(--wi-text-secondary)' };
}

const obsHeaderStyle = {
  padding: '6px 8px',
  color: 'var(--wi-text-secondary)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const obsCellStyle = {
  padding: '8px',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

/* ── Icons ─────────────────────────────────────────────────────────── */
function CopyIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ── Formatters ────────────────────────────────────────────────────── */
function formatTimestamp(iso) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso)).replace(',', ' at');
  } catch {
    return iso;
  }
}

/* Inject keyframes once (idempotent — guards against duplicate insertion in tests/HMR) */
if (typeof document !== 'undefined' && !document.getElementById('wi-cms-reporting-modal-anims')) {
  const style = document.createElement('style');
  style.id = 'wi-cms-reporting-modal-anims';
  style.textContent = `
    @keyframes wi-spin { to { transform: rotate(360deg); } }
    @keyframes wi-pulse {
      0%, 100% { box-shadow: 0 0 0 0 var(--wi-status-info-10); }
      50%      { box-shadow: 0 0 0 6px var(--wi-status-info-10); }
    }
  `;
  document.head.appendChild(style);
}
